import { HashLocationStrategy, LocationStrategy } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToasterModule, ToasterService } from 'angular2-toaster';
import { LoggerFactory } from './shared/loggerFactory';

import { AppComponent } from './app.component';
import { ROUTES } from './app.routes';

// App views
import { AppviewsModule } from './views/appviews/appviews.module';
import { SuperAdminModule } from './views/superadmin/superadmin.module';

// App modules/components
import { AngularFireModule } from '@angular/fire';
import { AngularFireAuthModule } from '@angular/fire/auth';
import { AngularFireDatabaseModule } from '@angular/fire/database';
import { AngularFireStorageModule } from '@angular/fire/storage';
import { AppConfig } from 'app/app.config';
import { IsAuthenticatedAuthGuard } from 'app/authGuards/isAuthenticated.authGuard';
import { IsSuperadminAuthGuard } from 'app/authGuards/isSuperadmin.authGuard';
import { IsVerifiedAuthGuard } from 'app/authGuards/isVerified.authGuard';
import { ModalModule } from 'app/components/modal/modal.module';
import { SharedModule } from 'app/components/shared.module';
import { GlobalErrorHandler } from 'app/globalErrorHandler';
import { PipesModule } from 'app/pipes/pipes.module';
import { AuthService } from 'app/services/auth.service';
import { AuthServiceErrorHandler } from 'app/services/authServiceErrorHandler';
import { Clock } from 'app/services/clock';
import { ConfigService } from 'app/services/config.service';
import { DatabaseService } from 'app/services/database/database.service';
import { RoleRepository } from 'app/services/database/role.repository';
import { StorageService } from 'app/services/database/storage.service';
import { UserRepository } from 'app/services/database/user.repository';
import { FileService } from 'app/services/files.service';
import { LocalizedToasterService } from 'app/services/localizedToasterService';
import { PresentationService } from 'app/services/presentation/presentation.service';
import { TokenInterceptor } from 'app/services/tokenInterceptor';
import { i18nLoader } from 'app/shared/i18n/i18nLoader';
import { CustomDatepickerConfig } from 'app/shared/i18n/ngx-bootstrap/bs-datepicker.config';
import { enCA } from 'app/shared/i18n/ngx-bootstrap/en-CA';
import { frCA } from 'app/shared/i18n/ngx-bootstrap/fr-CA';
import { DevicesModule } from 'app/views/devices/devices.module';
import { PresentationsModule } from 'app/views/presentations/presentations.module';
import { UserViewsModule } from 'app/views/user/userViews.module';
import { locale } from 'moment';
import { BsDatepickerModule, defineLocale, getSetGlobalLocale, TimepickerModule } from 'ngx-bootstrap';
import { BsDatepickerConfig } from 'ngx-bootstrap/datepicker/bs-datepicker.config';
import { environment } from '../environments/environment';
import { LayoutsModule } from './components/common/layouts/layouts.module';
import { LocalStorageService } from './services/local-storage.service';
import { FileHandler } from './services/presentation/fileHandler';
import { OptionsModule } from './views/options/options.module';
import { WidgetWrapperModule } from './views/presentations/widgets/widgetWrapper.module';
import { WebViewGuard } from './views/devices/web-view/web-view.guard';

@NgModule({
    declarations: [
        AppComponent
    ],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        FormsModule,
        HttpClientModule,
        BsDatepickerModule.forRoot(),
        TimepickerModule.forRoot(),
        SuperAdminModule.forRoot(),
        DevicesModule.forRoot(),
        PresentationsModule.forRoot(),
        LayoutsModule,
        AppviewsModule,
        UserViewsModule,
        OptionsModule,
        PipesModule,
        SharedModule.forRoot(),
        ToasterModule,
        RouterModule.forRoot(ROUTES),
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useClass: i18nLoader
            }
        }),
        AngularFireModule.initializeApp(environment.firebaseConfig),
        AngularFireAuthModule,
        AngularFireDatabaseModule,
        AngularFireStorageModule,
        ModalModule.forRoot()
    ],
    providers: [
        DatabaseService,
        StorageService,
        UserRepository,
        RoleRepository,
        {
            provide: LocationStrategy,
            useClass: HashLocationStrategy
        },
        {
            provide: ErrorHandler,
            useClass: GlobalErrorHandler
        },
        AuthServiceErrorHandler,
        {
            provide: ToasterService,
            useClass: LocalizedToasterService
        },
        PresentationService,
        FileService,
        LocalStorageService,
        WidgetWrapperModule.wrapHandler(FileHandler),
        AuthService,
        IsAuthenticatedAuthGuard,
        IsVerifiedAuthGuard,
        IsSuperadminAuthGuard,
        WebViewGuard,
        ConfigService,
        Clock,
        i18nLoader,
        LoggerFactory,
        {
            provide: APP_INITIALIZER,
            useFactory: appInitializer,
            deps: [ConfigService, LoggerFactory, TranslateService],
            multi: true
        },
        {
            provide: BsDatepickerConfig,
            useClass: CustomDatepickerConfig
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: TokenInterceptor,
            multi: true
        }
    ],
    bootstrap: [AppComponent]
})
export class AppModule { }

export function appInitializer(config: ConfigService, loggerFactory: LoggerFactory, translate: TranslateService) {
    return async () => {
        let logger = loggerFactory.create(`AppInitializer`);
        logger.debug('Initializing i18n');
        translate.setDefaultLang('fr-CA');
        let language = await config.get('defaultLanguage', 'fr-CA');
        logger.debug(`Default language is ${language}`);
        await translate.use(language).toPromise();
        locale(language);

        // The tool we use change the key somewhere in the code and fails to setup another tool with
        // the provided key (it normalize it lowercase...) so we register those lowercased locale first
        defineLocale('fr-ca', frCA);
        defineLocale('en-ca', enCA);
        defineLocale('fr-CA', frCA);
        defineLocale('en-CA', enCA);
        getSetGlobalLocale(language);
    };
}



// WEBPACK FOOTER //
// ./src/app/app.module.ts